import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useCallback } from "react";
import {
  apiRequest,
  queryClient,
  getAuthToken,
  setAuthToken,
  getRefreshToken,
  setRefreshToken,
  clearRefreshToken,
} from "./queryClient";
import {
  MasterLoginRequiredError,
  AccountLockedError,
  MfaCodeRequiredError,
  MfaInvalidCodeError,
  MfaSetupRequiredError,
  parseLoginErrorBody,
} from "./auth-errors";

import type { User, Organization } from "@shared/schema";

export type AuthUser = Omit<User, "password">;
export type AuthOrganization = Organization;

export const LIVE_CHAT_ROLES = new Set<string>(["live_admin", "live_power", "live_agent"]);
export function isLiveChatRole(role?: string | null): boolean {
  return !!role && LIVE_CHAT_ROLES.has(role);
}
export function isLiveChatAdminRole(role?: string | null): boolean {
  return role === "live_admin";
}

export type AuthMeResponse = {
  user: AuthUser & { permissions?: string[] };
  organization: AuthOrganization & { features?: string[] };
};

// .NET API returns: { id, email, userName, fullName, role, organizationId, permissions[], features[], redirectTo }
function mapRole(role: string | undefined | null): string {
  const r = (role || "").toLowerCase();
  if (r === "admin") return "super_admin";
  if (r === "orgadmin" || r === "org_admin") return "org_admin";
  if (r === "supervisor") return "supervisor";
  if (r === "liveagent" || r === "live_agent") return "live_agent";
  if (r === "auditor") return "auditor";
  if (r === "member" || r === "user") return "user";
  return r || "user";
}

function adaptMe(raw: any): AuthMeResponse | null {
  if (!raw || !raw.id) return null;
  return {
    user: {
      id: raw.id,
      email: raw.email,
      name: raw.fullName || raw.userName || raw.email,
      role: mapRole(raw.role) as any,
      organizationId: raw.organizationId,
      active: "true",
      permissions: Array.isArray(raw.permissions) ? raw.permissions : [],
    } as any,
    organization: {
      id: raw.organizationId,
      name: raw.organizationName || "",
      slug: raw.organizationSlug || "",
      plan: raw.organizationPlan || "starter",
      mode: "public",
      brandColor: raw.organizationBrandColor || "#0c3547",
      logo: raw.organizationLogo || null,
      tagline: raw.organizationTagline || null,
      features: Array.isArray(raw.features) ? raw.features : [],
      subscriptionStatus: raw.subscriptionStatus || null,
      requiresBilling: !!raw.requiresBilling,
      // True when this is a new org that hasn't completed the onboarding
      // role-selection step yet.  Used to resume interrupted onboarding.
      needsRolesOnboarding: !!raw.needsRolesOnboarding,
      // Module category keys that SuperAdmin has disabled platform-wide.
      // Empty for Super Admins (unrestricted) and when all modules are on.
      // Used by sidebar and route guards to hide module-specific UI.
      disabledModules: Array.isArray(raw.disabledModules) ? raw.disabledModules as string[] : [],
    } as any,
  };
}

export function useFeature(key: string): boolean {
  const { organization, user } = useAuth();
  if ((user as any)?.role === "super_admin") return true;
  return Array.isArray((organization as any)?.features) && (organization as any).features.includes(key);
}

export function hasPermission(user: any, perm: string): boolean {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  return Array.isArray(user.permissions) && user.permissions.includes(perm);
}

// Live-agent / supervisor console access. Mirrors the backend gate:
// anyone holding livechat.operate or livechat.supervise (admins get both
// via super_admin). End-users / auditors never qualify. Hidden route —
// not surfaced in any sidebar nav.
export function canUseAgentConsole(user: any): boolean {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  return (
    hasPermission(user, "livechat.operate") ||
    hasPermission(user, "livechat.supervise") ||
    hasPermission(user, "livechat.admin")
  );
}

// Supervisor capabilities (take over, reassign, monitor, agent snapshot).
export function isSupervisor(user: any): boolean {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  return (
    hasPermission(user, "livechat.supervise") ||
    hasPermission(user, "livechat.admin")
  );
}

export const AUTH_ME_KEY = ["/api/auth/me"] as const;

async function fetchAuthMe(): Promise<AuthMeResponse | null> {
  const res = await fetch("/api/auth/me", {
    credentials: "include",
    headers: (() => {
      const t = localStorage.getItem("dotnetbot.jwt");
      const h: Record<string, string> = {};
      if (t) h.Authorization = `Bearer ${t}`;
      return h;
    })(),
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(String(res.status));
  return adaptMe(await res.json());
}

/**
 * Fetches /api/auth/me into the shared query cache and resolves only when
 * user + organization + permissions + features are all present (they arrive
 * in this single response). Await this after storing a fresh token and
 * BEFORE navigating, so route guards and dashboard pages mount against fully
 * initialized auth state — no reload, no setTimeout, no sign-in flash.
 */
export async function primeAuth(): Promise<AuthMeResponse | null> {
  return queryClient.fetchQuery({
    queryKey: AUTH_ME_KEY,
    queryFn: fetchAuthMe,
    staleTime: 0,
  });
}

export function useAuth() {
  const { data, isLoading, error } = useQuery<AuthMeResponse | null>({
    queryKey: AUTH_ME_KEY,
    queryFn: fetchAuthMe,
    retry: false,
    // Permissions are owned by the Super Admin and can change server-side at
    // any time. Override the global staleTime:Infinity so the SPA reflects live
    // backend permissions (refetch on focus + periodically) instead of caching
    // a stale grant for the whole session. Kept slightly above zero so the
    // login handoff (primeAuth) and the app shell mounting a second useAuth
    // observer moments later share one fetch instead of firing twice.
    staleTime: 5_000,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
  return {
    user: data?.user ?? null,
    organization: data?.organization ?? null,
    isLoading,
    isAuthenticated: !!data?.user,
    error,
  };
}

export function useOrgBySlug(slug: string | null) {
  return useQuery<{ id: string; name: string; slug: string; mode: string; ssoEnforced: string; brandColor?: string; logo?: string } | null>({
    queryKey: ["/api/org-info", slug],
    queryFn: async () => {
      if (!slug) return null;
      const res = await fetch(`/api/public/landing/${encodeURIComponent(slug)}`);
      if (!res.ok) return null;
      const j = await res.json();
      return {
        id: j.id || j.organizationId || "",
        name: j.name || "",
        slug: j.slug || slug,
        mode: j.mode || "public",
        ssoEnforced: String(j.ssoEnforced || "false"),
        brandColor: j.brandColor,
        logo: j.logo,
      };
    },
    enabled: !!slug,
    retry: false,
  });
}

// Re-export so existing callers that import from auth.ts continue to work.
export {
  MasterLoginRequiredError,
  AccountLockedError,
  MfaCodeRequiredError,
  MfaInvalidCodeError,
  MfaSetupRequiredError,
  parseLoginErrorBody,
} from "./auth-errors";

export function useLogin() {
  return useMutation({
    mutationFn: async (data: { email: string; password: string; totpCode?: string }) => {
      // .NET API expects { identifier, password, totpCode? } and returns { token, user, redirectTo }
      const res = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: data.email,
          password: data.password,
          totpCode: data.totpCode,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw parseLoginErrorBody(res.status, body);
      if (body?.token) setAuthToken(body.token);
      // Store the rotating refresh token (null-safe — older API versions omit it).
      if (body?.refreshToken) setRefreshToken(body.refreshToken);
      // Fully initialize auth state (user/org/permissions/features) before
      // the mutation resolves — callers can navigate immediately afterwards
      // without a refresh, a flash of the sign-in page, or a guard race.
      await primeAuth();
      return body;
    },
  });
}

/**
 * Silently refreshes the access token using the stored refresh token.
 *
 * On success the new access + refresh tokens are stored and `true` is returned.
 * On any failure (network, expired/revoked token, server error) `false` is
 * returned — the caller decides whether to prompt re-authentication.
 * Never throws.
 */
export async function refreshSession(): Promise<boolean> {
  const token = getRefreshToken();
  if (!token) return false;
  try {
    const res = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // Intentionally no `credentials: "include"` — this endpoint is
      // AllowAnonymous and the refresh token in the body is the credential.
      body: JSON.stringify({ token }),
    });
    if (!res.ok) return false;
    const body = await res.json().catch(() => ({}));
    if (!body?.accessToken || !body?.refreshToken) return false;
    setAuthToken(body.accessToken);
    setRefreshToken(body.refreshToken);
    return true;
  } catch {
    return false;
  }
}

export function useRegister() {
  return useMutation({
    mutationFn: async (data: { email: string; password: string; name: string; organizationName: string }) => {
      // Routes through the new /signup endpoint which creates the org
      // in 'pending' state. The billing hard-lock then funnels the
      // browser to /billing/select-plan on next /api/auth/me.
      const res = await apiRequest("POST", "/api/auth/signup", {
        email: data.email,
        password: data.password,
        fullName: data.name,
        organizationName: data.organizationName,
      });
      const body = await res.json();
      if (body?.token) setAuthToken(body.token);
      await primeAuth();
      return body;
    },
  });
}

// ── Logout ────────────────────────────────────────────────────────────────────

const AUTH_BROADCAST_CHANNEL = "dotnetbot.auth";
// A BroadcastChannel message also reaches other listeners in the sender tab.
const AUTH_TAB_ID = crypto.randomUUID();

/**
 * Synchronously wipes the local session: JWT, every private React Query cache
 * entry, and marks the in-flight query for /api/auth/me as cancelled.
 *
 * Call this BEFORE any await or navigation so there is zero frame of exposure.
 * Exported so useAuthBarrier (cross-tab/bfcache) can reuse the same teardown.
 */
export function performLogoutSync(): void {
  // Invalidate visitor capabilities across tabs as well as this tab.
  try {
    sessionStorage.removeItem("dotnetbot.visitor-live");
    localStorage.removeItem("dotnetbot.visitor-browser");
    window.dispatchEvent(new Event("visitor-identity-changed"));
  } catch {}
  setAuthToken(null);
  // Also clear the refresh token so it cannot be replayed after logout.
  // The server revokes it server-side too (POST /api/auth/logout does a
  // bulk revoke for the user), but clearing locally ensures no local replay.
  clearRefreshToken();
  // Mark all active queries cancelled in TanStack's internal state.
  queryClient.cancelQueries();
  // Remove every cached response. After this there is no private data in memory.
  queryClient.clear();
  // Stamp the auth slot as null so any still-mounted component that subscribes
  // to AUTH_ME_KEY receives null (unauthenticated) rather than undefined/loading.
  queryClient.setQueryData(AUTH_ME_KEY as unknown as string[], null);
}

function broadcastLogout(): void {
  try {
    const bc = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    bc.postMessage({ type: "logout", sender: AUTH_TAB_ID });
    bc.close();
  } catch {
    // BroadcastChannel unsupported (very old Safari) — cross-tab handled by
    // the storage-event fallback in useAuthBarrier.
  }
}

export function useLogout() {
  return useMutation({
    mutationFn: async () => {
      // ── Step 1: capture the access token before wiping it ─────────────────
      // The backend logout endpoint is [Authorize]. We must send the token as
      // the Authorization header so the server can revoke the UserSession and
      // all refresh tokens. If we clear storage first and send no header, JWT-
      // only users receive a 401 and the server never executes the revocation
      // logic — leaving the session live, which allows a window-focus refetch
      // of /api/auth/me to re-authenticate the user while we are still awaiting
      // the (failed) logout fetch, causing the "multiple clicks needed" symptom.
      const accessToken = getAuthToken();

      // ── Step 2: synchronously wipe all private state ──────────────────────
      // This must happen before any await so no protected component can render
      // in the cleared-token / cleared-cache state even for a single frame.
      performLogoutSync();

      // ── Step 3: await server-side session revocation ──────────────────────
      // Must be awaited — not fire-and-forget. If we navigate before the
      // server confirms the logout, the /api/auth/me refetch triggered by
      // window-focus or the 60-second interval could race the logout request:
      // the server still sees a valid session and returns the user, causing an
      // immediate redirect back to the dashboard. Awaiting guarantees the server
      // has cleared the session and cookie before the browser loads /login.
      try {
        await fetch("/api/auth/logout", {
          method: "POST",
          credentials: "include",
          // Send the captured token — even though we already cleared it from
          // storage — so the [Authorize] check on the endpoint passes and the
          // server executes its full revocation (UserSession + refresh tokens +
          // cookie SignOut) for every auth mode including JWT-only.
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });
      } catch {
        // Network failure is acceptable — local state is already wiped.
      }

      // ── Step 4: notify other tabs after the server has cleared the cookie.
      // Otherwise a remote tab can reach /login with a still-valid cookie.
      broadcastLogout();

      // ── Step 5: hard-replace so the authenticated URL leaves history ───────
      window.location.replace("/login");
    },
  });
}

// ── Cross-tab + bfcache auth barrier ─────────────────────────────────────────

/**
 * Mount once inside AuthenticatedApp. Handles two orthogonal concerns:
 *
 * 1. Cross-tab logout:
 *    • BroadcastChannel receives a "logout" message from another tab and
 *      immediately wipes state + navigates to /login in this tab too.
 *    • Storage-event fallback covers same-origin tabs in Safari < 15.4.
 *
 * 2. bfcache page restore:
 *    • The index.html inline script already shows the auth-barrier overlay
 *      and redirects immediately when there is no token.
 *    • When a token is present we still need to re-validate the session
 *      server-side before revealing the frozen dashboard. This hook calls
 *      onBfcacheBlock() to make React render a neutral loading screen, then
 *      re-fetches /api/auth/me. On success it calls onBfcacheUnblock();
 *      on failure it clears state and navigates to /login.
 */
export function useAuthBarrier(
  onBfcacheBlock: () => void,
  onBfcacheUnblock: () => void,
): void {
  // Capture stable refs so the effect closure never goes stale.
  const blockRef = { current: onBfcacheBlock };
  const unblockRef = { current: onBfcacheUnblock };
  blockRef.current = onBfcacheBlock;
  unblockRef.current = onBfcacheUnblock;

  useEffect(() => {
    // ── Cross-tab logout via BroadcastChannel ─────────────────────────────
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      bc.onmessage = (e: MessageEvent) => {
        if (e.data?.type === "logout" && e.data?.sender !== AUTH_TAB_ID) {
          performLogoutSync();
          window.location.replace("/login");
        }
      };
    } catch {
      // BroadcastChannel unavailable — storage event fallback below covers it.
    }

    // Safari / older browsers: watch for the JWT key being removed in another tab.
    const onStorage = (e: StorageEvent) => {
      if (e.key === "dotnetbot.jwt" && e.newValue === null) {
        performLogoutSync();
        window.location.replace("/login");
      }
    };
    window.addEventListener("storage", onStorage);

    // ── bfcache page restore ──────────────────────────────────────────────
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;

      // The index.html inline script already navigated away when there was
      // no token. Double-check here in case the inline script ran before
      // localStorage was populated on a very slow device.
      const hasToken = (() => {
        try { return !!localStorage.getItem("dotnetbot.jwt"); } catch { return false; }
      })();

      if (!hasToken) {
        window.location.replace("/login");
        return;
      }

      // Token present: tell React to show the neutral loading screen instead
      // of the frozen dashboard, hide the barrier overlay, then re-validate.
      blockRef.current();
      const el = document.getElementById("auth-barrier");
      if (el) el.style.display = "none";

      fetchAuthMe()
        .then((result) => {
          if (!result?.user) {
            performLogoutSync();
            window.location.replace("/login");
          } else {
            unblockRef.current();
          }
        })
        .catch(() => {
          performLogoutSync();
          window.location.replace("/login");
        });
    };

    window.addEventListener("pageshow", onPageShow as EventListener);

    return () => {
      bc?.close();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pageshow", onPageShow as EventListener);
    };
  }, []); // intentionally empty — effect runs once; callbacks captured via ref pattern above
}
